import { gql } from '@apollo/client';
import IconError from '@apollo/icons/default/IconError.svg';
import IconSuccess from '@apollo/icons/default/IconSuccess.svg';
import IconTime from '@apollo/icons/default/IconTime.svg';
import IconUnavailable from '@apollo/icons/default/IconUnavailable.svg';
import {
  Badge,
  Link,
  Spinner,
  StatusBadge,
  StatusBadgeProps,
} from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { LaunchStatus, OrderStatus } from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import {
  LaunchesListFragment,
  computeLaunchesListInfo,
} from '../../../LaunchesList/helpers/computeLaunchesListInfo';
import { LaunchesListSummaryItem } from '../../../LaunchesList/LaunchesListItem/LaunchesListSummaryItem';
import { LaunchIDHash } from '../../../LaunchIDHash';
import { LaunchUpdatedSubgraphs } from '../../../LaunchUpdatedSubgraphs';

export const getLaunchStatusText = ({
  status,
  superseded,
}: {
  status: GraphQLTypes.LaunchStatus;
  superseded: boolean;
}) => {
  switch (status) {
    case LaunchStatus.LAUNCH_INITIATED:
      return 'Launch initiated';
    case LaunchStatus.LAUNCH_COMPLETED:
      return superseded ? 'Launch superseded' : 'Launch completed';
    case LaunchStatus.LAUNCH_FAILED:
      return 'Launch failed';
    // case LaunchStatus.LAUNCH_SUPERSEDED:
    //   return 'Launch superseded';
    default:
      assertUnreachable(status);
  }
};

export const getOrderStatusBadgeContent = (
  status: OrderStatus,
): {
  title: string;
  colorScheme?: StatusBadgeProps['colorScheme'];
  icon?: React.ReactElement;
} => {
  switch (status) {
    case OrderStatus.COMPLETED:
      return {
        title: 'Deployed',
        colorScheme: 'green',
      };
    case OrderStatus.PENDING:
      return {
        title: 'Deploying',
        icon: <Spinner size="tiny" />,
        colorScheme: 'gray',
      };
    case OrderStatus.ERRORED:
      return {
        title: 'Deployment failed',
        colorScheme: 'red',
      };
    case OrderStatus.ROLLING_BACK:
      return {
        title: 'Rolling back deployment',
        icon: <Spinner size="tiny" />,
        colorScheme: 'gray',
      };
    case OrderStatus.SUPERSEDED:
      return {
        title: 'Deployment Superseded',
        colorScheme: 'green',
      };
    default:
      return assertUnreachable(status);
  }
};

export const LaunchHistoryListItemFragment = gql`
  fragment LaunchHistoryListItemFragment on Launch {
    id
    shortenedID
    createdAt
    completedAt
    isPublished
    subgraphChanges {
      name
      type
    }
    status
    build {
      id
      result {
        ... on BuildFailure {
          errorCount
        }
        __typename
      }
    }
    schemaTag {
      id
      diffToPrevious {
        severity
        numberOfAffectedOperations
      }
    }
    supersededBy {
      id
    }
    order @skip(if: $skipOrder) {
      __typename
      ... on Order {
        id
        status
        reason
      }
    }
    ...LaunchesListFragment
  }
  ${LaunchesListFragment}
`;

interface Props {
  launch: GraphQLTypes.LaunchHistoryListItemFragment;
  selected?: boolean;
  activeSchemaPublishId?: string;
  isMostRecentLaunch: Boolean;
  isCloudSupergraph: Boolean;
}

export const LaunchHistoryListItem = ({
  launch,
  selected,
  activeSchemaPublishId,
  isMostRecentLaunch,
  isCloudSupergraph,
}: Props) => {
  const {
    id,
    build,
    schemaTag,
    isPublished,
    status,
    shortenedID,
    subgraphChanges,
    order,
  } = launch;

  const buildErrorCount =
    build?.result?.__typename === 'BuildFailure' && build.result.errorCount;
  const superseded = Boolean(launch.supersededBy?.id);
  const isMostRecentlyPublished = activeSchemaPublishId === schemaTag?.id;
  const launchesListInfo = computeLaunchesListInfo({
    parentLaunch: launch,
    kind: 'launch',
  });

  // show router status badge if this is the most recent launch,
  // and always show the badge if the router deployment failed.
  const showRouterStatusBadge =
    isCloudSupergraph &&
    order?.__typename === 'Order' &&
    (isMostRecentLaunch ||
      order.status === OrderStatus.ERRORED ||
      order.status === OrderStatus.ROLLING_BACK);

  return (
    <div
      className={classnames(
        'cursor-pointer overflow-hidden border-b border-l-4 border-b-primary px-6 py-4',
        {
          'border-l-transparent hover:bg-secondary': !selected,
          'border-l-selected bg-secondary': selected,
        },
      )}
    >
      <div className="flex">
        <div className="flex items-start overflow-hidden">
          <LaunchStatusIcon
            className="mr-2 mt-1 size-4"
            status={status}
            superseded={superseded}
          />
          <div className="overflow-hidden">
            <div className="flex items-center">
              <span
                className={classnames('truncate text-sm text-primary', {
                  'text-info': status === LaunchStatus.LAUNCH_INITIATED,
                })}
              >
                {getLaunchStatusText({ status, superseded })}
              </span>
              <LaunchIDHash
                launchID={id}
                shortLaunchID={shortenedID}
                className="mx-2 truncate"
              />
            </div>
            <span className="mt-1 flex flex-nowrap align-middle text-sm text-secondary">
              {subgraphChanges && subgraphChanges.length > 0 && (
                <>
                  <LaunchUpdatedSubgraphs subgraphChanges={subgraphChanges} />
                  <span className="mr-2 text-secondary">•</span>
                </>
              )}
              <span className="mr-1 text-sm text-secondary">Initiated</span>
              <RelativeTimestamp
                className="truncate text-sm font-semibold text-secondary"
                value={launch.createdAt}
              />
            </span>
            {launchesListInfo.launches.length ? (
              <LaunchesListSummaryItem
                className="mt-4"
                launchesInfo={launchesListInfo}
              />
            ) : null}
          </div>
        </div>
        <div className="ml-2 flex flex-1 items-start justify-end">
          {isPublished && isMostRecentlyPublished && (
            <Badge variant="success" className="ml-2">
              Published
            </Badge>
          )}
          {buildErrorCount && buildErrorCount > 0 && (
            <Badge variant="error" className="ml-2">
              {buildErrorCount} error
              {buildErrorCount !== 1 ? 's' : ''}
            </Badge>
          )}
          {showRouterStatusBadge && (
            <RouterOrderStatus
              status={order.status}
              reason={order.reason && order.reason}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const RouterOrderStatus = ({
  status,
  reason,
}: {
  status: OrderStatus;
  reason: String | null;
}) => {
  const { title, colorScheme, icon } = getOrderStatusBadgeContent(status);
  const handleClick = (e: React.MouseEvent) => e.stopPropagation();

  // show router status tooltip only if the router deployment failed or
  // is rolling back.
  const showRouterStatusTooltip =
    status === OrderStatus.ERRORED || status === OrderStatus.ROLLING_BACK;

  return (
    <Tooltip
      label={
        <p>
          Error: {reason && reason}. Try{' '}
          <Link
            isExternal
            to={DocsRouter.path('GraphOSCloudRouting', {
              hash: 'cloud-launches',
            })}
            onClick={handleClick}
          >
            republishing your schema
          </Link>{' '}
          or <ContactSupportLink>contact us</ContactSupportLink>.{' '}
        </p>
      }
      placement="top"
      interactive
      disabled={!showRouterStatusTooltip}
    >
      <StatusBadge icon={icon} colorScheme={colorScheme}>
        {title}
      </StatusBadge>
    </Tooltip>
  );
};

const LaunchStatusIcon = ({
  className,
  status,
  superseded,
}: {
  className?: string;
  status: GraphQLTypes.LaunchStatus;
  superseded: boolean;
}) => {
  switch (status) {
    case LaunchStatus.LAUNCH_INITIATED:
      return <IconTime className={classnames(className, 'text-icon-info')} />;
    case LaunchStatus.LAUNCH_COMPLETED:
      // Ideally we'd migrate these into the schema using unique statuses
      return superseded ? (
        <IconUnavailable
          className={classnames(className, 'text-icon-disabled')}
        />
      ) : (
        <IconSuccess className={classnames(className, 'text-icon-success')} />
      );
    case LaunchStatus.LAUNCH_FAILED:
      return <IconError className={classnames(className, 'text-icon-error')} />;
    default:
      assertUnreachable(status);
  }
};
